import {
  useFormContext,
  type ControllerFieldState,
  type ControllerRenderProps,
} from 'react-hook-form';
import type { ReactNode } from 'react';
import { NumberMark } from '@/components/common/NumberMark';
import { RequiredMark } from '@/components/common/RequiredMark';
import { Badge } from '@/components/ui/badge';
import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { SURVEY_QUESTION_TYPE_LABELS } from '../../constants/survey.constants';
import {
  QuestionType,
  type SurveyAnswerFormValues,
  type SurveyQuestionDetail,
} from '../../types/survey.types';
import { answerFieldName } from '../../utils/surveyAnswerForm';
import { CheckboxAnswerInput } from './CheckboxAnswerInput';
import { DropdownAnswerInput } from './DropdownAnswerInput';
import { McqAnswerInput } from './McqAnswerInput';
import { RatingAnswerInput } from './RatingAnswerInput';
import { TextAnswerInput } from './TextAnswerInput';

interface SurveyAnswerFieldProps {
  question: SurveyQuestionDetail;
  index: number;
}

type AnswerField = ControllerRenderProps<SurveyAnswerFormValues>;

export function SurveyAnswerField({ question, index }: SurveyAnswerFieldProps) {
  const { control } = useFormContext<SurveyAnswerFormValues>();

  return (
    <FormField
      control={control}
      name={answerFieldName(question.id)}
      render={({ field, fieldState }) => (
        <FormItem className="flex flex-col gap-3 rounded-xl border bg-card p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2">
              <NumberMark value={index + 1} className="mt-0.5" />
              <FormLabel className="text-base font-medium leading-snug wrap-break-word">
                {question.text}
                {question.isRequired ? (
                  <>
                    <RequiredMark />
                    <span className="sr-only"> (required)</span>
                  </>
                ) : null}
              </FormLabel>
            </div>
            <Badge variant="outline" className="shrink-0">
              {SURVEY_QUESTION_TYPE_LABELS[question.type]}
            </Badge>
          </div>

          {renderAnswerControl(question, field, fieldState)}

          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function renderAnswerControl(
  question: SurveyQuestionDetail,
  field: AnswerField,
  fieldState: ControllerFieldState,
): ReactNode {
  const invalid = fieldState.invalid;

  switch (question.type) {
    case QuestionType.TEXT:
      return (
        <TextAnswerInput
          question={question}
          value={field.value as string}
          onChange={field.onChange}
          onBlur={field.onBlur}
          name={field.name}
          invalid={invalid}
        />
      );
    case QuestionType.RATING:
      return (
        <RatingAnswerInput
          value={field.value as string}
          onChange={field.onChange}
          invalid={invalid}
        />
      );
    case QuestionType.MCQ:
      return (
        <McqAnswerInput
          name={field.name}
          options={question.options}
          value={field.value as string}
          onChange={field.onChange}
          invalid={invalid}
        />
      );
    case QuestionType.DROPDOWN:
      return (
        <DropdownAnswerInput
          options={question.options}
          value={field.value as string}
          onChange={field.onChange}
          invalid={invalid}
        />
      );
    case QuestionType.CHECKBOX:
      return (
        <CheckboxAnswerInput
          name={field.name}
          options={question.options}
          value={(field.value as string[]) ?? []}
          onChange={field.onChange}
          invalid={invalid}
        />
      );
  }
}
